import React, { useState, useMemo, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  getApplications,
  getMaxSequences,
  getLoading,
  getIsAdmin,
  getGeminiResult,
  getIsLoadingBot,
} from "../../../selectors";
import { createFunctionAction, geminiChatAction, modifyFunctionAction } from "../../../actions";
import {
  TextField,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Switch,
  FormControlLabel,
  Tooltip,
  Button
} from "@material-ui/core";
import { getLocalizedString } from "@icarius-localization/strings";
import ButtonDialogAction from "@icarius-common/buttonDialogAction";
import { openSnackbarAction } from "@icarius-common/snackbar/actions";
import useHandleConceptsFormulasForm from "./useHandleFunctionForm";
import CommonPage from "@icarius-common/commonPage";
import paths from "@icarius-router/paths";
import CustomIconButton from "@icarius-common/abmComponents/customIconButton";
import TabMenu from "@icarius-common/tabMenu";
import TextFieldMultiline from "@icarius-common/textFieldMultiline";
import AllInclusiveIcon from '@material-ui/icons/AllInclusive';
import FormulaAiDialog from "../../formulaAiDialog";
import useIsPathAvailable from "@icarius-utils/hooks/useIsPathAvailable";

const gridStyle = { height: 75, padding: "0px 10px" };

const Form = (props) => {

  const {
    data,
    handleClose,
  } = props;

  const dispatch = useDispatch();
  const isAdmin = useSelector(getIsAdmin);

  const loading = useSelector(getLoading);
  const applications = useSelector(getApplications);
  const maxSequences = useSelector(getMaxSequences);
  const geminiContext = useSelector(getGeminiResult);
  const isLoadingBot = useSelector(getIsLoadingBot);
  const isAiQueriesEnabled = useIsPathAvailable(paths.aiQueryAnalysisButton);

  const [optionToShow, setOptionToShow] = useState(0);
  const [aiDialogOpen, setAiDialogOpen] = useState(false);

  const create = (dataToSend) => {
    dispatch(createFunctionAction(dataToSend))
      .then((res) => res?.status === 200 && handleClose());
  }

  const modify = (formData) => {
    dispatch(modifyFunctionAction(formData))
      .then((res) => res?.status === 200 && handleClose());
  }

  const handleSendMessage = async (data) => {
    return dispatch(geminiChatAction(data));
  }

  const openValidationError = () => {
    dispatch(openSnackbarAction({ msg: getLocalizedString("invalidData"), severity: "error", duration: 10000 }));
  }

  const openCodeError = () => {
    dispatch(openSnackbarAction({
      msg: getLocalizedString("functionNameRestrictions"),
      severity: "error",
      duration: 10000
    }));
  }

  const {
    isCreate,
    formData,
    setFormValue,
    submit
  } = useHandleConceptsFormulasForm(data, maxSequences, create, modify, openValidationError, openCodeError);

  const goBackItem = () =>
    <CustomIconButton
      title={getLocalizedString("goBack")}
      onClick={handleClose}
      type={"goBack"}
    />

  const getLabelForUsePersonalizedFormula = (formData) => {
    if (!formData?.formulaPersonalizada) return getLocalizedString("activeStandard");
    return getLocalizedString("activeCustomization");
  }

  useEffect(() => {
    if (Boolean(data?.formulaPersonalizada)) {
      setOptionToShow(1);
    }
  }, [data]);

  const sequenceArr = useMemo(() => {
    if (!formData.application) return [];

    const amount = (formData.application === "P" || formData.application === "C") ? 1 : 99;

    return Array(amount).fill().map((_, i) => ({ key: i + 1, value: i + 1 }));
  }, [formData.application])

  const queryPreset = isCreate ? "" : data?.name || "";

  return (
    <CommonPage
      columnDefPage={paths.conceptsAndFormulas}
      title={getLocalizedString("calculationsFunctions")}
      isNotGridPage
      menuItems={[goBackItem]}
      isLoading={loading}
    >
      <Grid container item xs={12} direction="row" style={{ width: "95%", margin: "auto" }}>
        <Grid container item xs={12}>
          <Grid container item alignItems="center" xs={12} sm={6} md={3} style={gridStyle}>
            <TextField
              disabled={!Boolean(isCreate)}
              required
              fullWidth
              margin={"none"}
              label={getLocalizedString("functionNameLongAlt")}
              onChange={(e) => setFormValue(e.target.value, "name")}
              value={formData.name}
              inputProps={{ maxLength: 20 }}
            />
          </Grid>
          <Grid container item alignItems="center" xs={12} sm={6} md={3} style={gridStyle}>
            <TextField
              required
              fullWidth
              margin={"none"}
              label={getLocalizedString("jobDescription")}
              value={formData.description}
              onChange={(e) => setFormValue(e.target.value, "description")}
              inputProps={{ maxLength: 100 }}
            />
          </Grid>
          <Grid container item alignItems="center" xs={12} sm={6} md={3} style={gridStyle}>
            <FormControl style={{ width: "100%" }}>
              <InputLabel required id={`label-application-${data ? data.internalCode : "new"}`}>{getLocalizedString("application")}</InputLabel>
              <Select
                value={formData.application}
                labelId={`label-application-${data ? data.internalCode : "new"}`}
                id={`select-application-${data ? data.internalCode : "new"}`}
                onChange={(e) => setFormValue(e.target.value, "application")}
                margin={"none"}
              >
                {
                  applications && applications.map(item => (
                    <MenuItem
                      className={"whiteText"}
                      key={item.key}
                      value={item.key}>
                      {item.value}
                    </MenuItem>
                  ))
                }
              </Select>
            </FormControl>
          </Grid>
          <Grid container item alignItems="center" xs={12} sm={6} md={3} style={gridStyle}>
            <FormControl style={{ width: "100%" }}>
              <InputLabel required id={`label-seq-${data ? data.internalCode : "new"}`}>{getLocalizedString("calculationSequenceUppercase")}</InputLabel>
              <Select
                value={formData.sequence}
                labelId={`label-seq-${data ? data.internalCode : "new"}`}
                id={`select-seq-${data ? data.internalCode : "new"}`}
                onChange={(e) => setFormValue(e.target.value, "sequence")}
                margin={"none"}
              >
                {
                  sequenceArr.map(item => (
                    <MenuItem
                      className={"whiteText"}
                      key={item.key}
                      value={item.key}>
                      {item.value}
                    </MenuItem>
                  ))
                }
              </Select>
            </FormControl>
          </Grid>
          <Grid container item xs={12} direction="row" justify="space-between" alignItems="center" style={{ marginTop: 15, marginLeft: 10 }}>
            <Grid container item xs={12} md={7}>
              <Grid container item xs={12} md={6}>
                <TabMenu
                  options={[getLocalizedString("standard"), getLocalizedString("customized")]}
                  optionSelected={optionToShow}
                  onClick={setOptionToShow}
                />
              </Grid>
            </Grid>
            {
              Boolean(formData.formulaPersonalizada) &&
              <Grid style={{ paddingTop: 5 }} container direction="row" justify="flex-end" item md={5} xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      label={getLocalizedString("activeCustomization")}
                      checked={true}
                      color="primary"
                    />
                  }
                  label={getLabelForUsePersonalizedFormula(formData)}
                  labelPlacement="start"
                  style={{ marginRight: 5 }}
                />
              </Grid>
            }
          </Grid>
          {
            optionToShow === 0 &&
            <Grid container item alignItems="center" xs={12} style={{ padding: "20px 10px" }}> {/* Formula */}
              <TextFieldMultiline
                fullWidth
                label={getLocalizedString("formula")}
                rows={25}
                margin={"none"}
                value={formData.formula}
                inputProps={{ spellCheck: 'false' }}
                onChange={(e) => isAdmin && setFormValue(e.target.value, "formula")}
              />
            </Grid>
          }
          {
            optionToShow === 1 &&
            <Grid container item alignItems="center" xs={12} style={{ padding: "20px 10px" }}> {/* Formula personalizada */}
              <TextFieldMultiline
                fullWidth
                label={getLocalizedString("customFormulaUppercase")}
                rows={25}
                margin={"none"}
                value={formData.formulaPersonalizada}
                onChange={(e) => setFormValue(e.target.value, "formulaPersonalizada")}
                inputProps={{ spellCheck: 'false' }}
              />
            </Grid>
          }
        </Grid>
        {
          isAiQueriesEnabled && (formData.formula || formData.formulaPersonalizada) &&
          <Tooltip title={getLocalizedString("explainFormulaWithAI")}>
            <Button
              variant={"contained"}
              disableRipple={true}
              className="whiteText"
              component={"span"}
              onClick={() => setAiDialogOpen(true)}
              style={{ marginLeft: 5 }}
            >
              <AllInclusiveIcon style={{ width: 24, height: 24 }} />
            </Button>
          </Tooltip>
        }
        <Grid container item xs={12} justify={"flex-end"} alignItems="center" style={{ marginBottom: 35 }}>
          <ButtonDialogAction isAccept onClick={submit} text={getLocalizedString("save")} />
        </Grid>
      </Grid>
      <FormulaAiDialog
        key={queryPreset}
        open={aiDialogOpen}
        isLoading={Boolean(isLoadingBot[queryPreset])}
        queryPreset={queryPreset}
        geminiContext={geminiContext}
        formulaText={formData.formula || formData.formulaPersonalizada}
        handleSendMessage={handleSendMessage}
        handleClose={() => setAiDialogOpen(false)}
      />
    </CommonPage>
  );
}

export default Form;
